import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

const UpcomingEventsContext = createContext();

export const useUpcomingEventsData = () => useContext(UpcomingEventsContext);

// const DATA_VERSION = 'v1'; // Increment this to force a data reset

export const UpcomingEventsProvider = ({ children }) => {
    const [rows, setRows] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [economicResponse, earningsResponse] = await Promise.all([
                    axios.get(`${API_URL}/upcoming-events/economic`),
                    axios.get(`${API_URL}/upcoming-events/earnings`)
                ]);

                const allRows = [
                    ...(economicResponse.data || []),
                    ...(earningsResponse.data || [])
                ];
                console.log(`[Trace] Fetched ${economicResponse.data?.length || 0} Economic Events and ${earningsResponse.data?.length || 0} Earnings Events from API.`);
                setRows(allRows);
            } catch (error) {
                console.error("Error fetching upcoming events data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const updateRow = async (index, updatedRow) => {
        const rowToUpdate = rows[index];
        if (!rowToUpdate.id) {
            console.error("Cannot update row without ID");
            return;
        }

        try {
            // Optimistic update
            const newRows = [...rows];
            newRows[index] = updatedRow;
            setRows(newRows);

            const endpoint = updatedRow.Category === "EARNINGS"
                ? `/upcoming-events/earnings/${rowToUpdate.id}`
                : `/upcoming-events/economic/${rowToUpdate.id}`;

            await axios.put(`${API_URL}${endpoint}`, {
                category: updatedRow.Category,
                event: updatedRow.Event,
                tag: updatedRow.Tag,
                headline: updatedRow.Headline,
                details: updatedRow.Details,
                date: updatedRow.Time, // Map Time back to date for backend
                impact: updatedRow.Impact,
                interpretation: updatedRow.Interpretation,
                assets: updatedRow.Assets,
                source: updatedRow.Source
            });
        } catch (error) {
            console.error("Error updating upcoming event:", error);
            window.location.reload();
        }
    };

    const deleteRow = async (index) => {
        const rowToDelete = rows[index];
        if (!rowToDelete.id) {
            console.error("Cannot delete row without ID");
            return;
        }

        try {
            setRows(prev => prev.filter((_, i) => i !== index));

            const endpoint = rowToDelete.Category === "EARNINGS"
                ? `/upcoming-events/earnings/${rowToDelete.id}`
                : `/upcoming-events/economic/${rowToDelete.id}`;

            await axios.delete(`${API_URL}${endpoint}`);
        } catch (error) {
            console.error("Error deleting upcoming event:", error);
            window.location.reload();
        }
    };

    const addRow = async (category = "ECONOMIC") => {
        const newRowData = {
            category: category,
            event: "MACRO_DATA",
            tag: "Tag",
            headline: "New Headline",
            details: "New Details",
            date: new Date().toISOString().slice(0, 16), // Default to current time YYYY-MM-DDTHH:MM
            impact: "Low",
            interpretation: "New Interpretation",
            assets: "EUR/USD",
            source: "Source"
        };

        try {
            const endpoint = category === "EARNINGS"
                ? `/upcoming-events/earnings`
                : `/upcoming-events/economic`;

            const response = await axios.post(`${API_URL}${endpoint}`, newRowData);
            if (response.data && response.data.id) {
                const newRow = {
                    id: response.data.id,
                    Category: newRowData.category,
                    Event: newRowData.event,
                    Tag: newRowData.tag,
                    Headline: newRowData.headline,
                    Details: newRowData.details,
                    Time: newRowData.date, // Map date to Time
                    Impact: newRowData.impact,
                    Interpretation: newRowData.interpretation,
                    Assets: newRowData.assets,
                    Source: newRowData.source
                };
                setRows(prev => [...prev, newRow]);
            }
        } catch (error) {
            console.error("Error creating upcoming event:", error);
        }
    };

    const resetData = async () => {
        setIsLoading(true);
        try {
            // Artificial delay for UX feedback
            await new Promise(resolve => setTimeout(resolve, 400));
            const [economicResponse, earningsResponse] = await Promise.all([
                axios.get(`${API_URL}/upcoming-events/economic`),
                axios.get(`${API_URL}/upcoming-events/earnings`)
            ]);

            const allRows = [
                ...(economicResponse.data || []),
                ...(earningsResponse.data || [])
            ];
            setRows(allRows);
        } catch (error) {
            console.error("Error reloading upcoming events data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const setAllData = (newRows) => {
        setRows(newRows);
    };

    const reorderRows = (startIndex, endIndex) => {
        setRows(prev => {
            const result = Array.from(prev);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        });
    };

    const [impactFilter, setImpactFilter] = useState('All');

    const filteredRows = React.useMemo(() => {
        if (impactFilter === 'All') return rows;

        return rows.filter(row => {
            if (!row.Impact) return false;
            const impact = row.Impact;

            // Logic: High matches "High" and "High/Medium"
            // Medium matches "Medium", "High/Medium", "Medium/Low"

            switch (impactFilter) {
                case 'Very High':
                    return impact.includes('Very High');
                case 'High':
                    return impact.includes('High');
                case 'Medium':
                    return impact.includes('Medium');
                case 'Low':
                    return impact.includes('Low');
                default:
                    return true;
            }
        });
    }, [rows, impactFilter]);

    // Helper to find the original index since tabs filter the data
    const getOriginalIndex = (row) => {
        return rows.findIndex(r => r === row);
    };

    return (
        <UpcomingEventsContext.Provider value={{
            rows,
            filteredRows,
            impactFilter,
            setImpactFilter,
            updateRow,
            deleteRow,
            addRow,
            resetData,
            setAllData,
            reorderRows,
            getOriginalIndex,
            isLoading
        }}>
            {children}
        </UpcomingEventsContext.Provider>
    );
};
